import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage, LANGUAGES } from '../../../i18n/LanguageContext';
import { ColorTheme } from '../../../themes/colors';

interface LanguageDropdownProps {
  theme: ColorTheme;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ theme }) => {
  const { language, setLanguage, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find(l => l.code === language)!;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 10px',
          borderRadius: '8px',
          border: `1px solid ${theme.cardBorder}`,
          background: isOpen ? theme.navLinkHover : 'transparent',
          color: theme.textSecondary,
          cursor: 'pointer',
          fontSize: '0.82rem',
          fontWeight: 500,
          transition: 'all 0.15s ease',
          whiteSpace: 'nowrap' as const,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = theme.navLinkHover; }}
        onMouseLeave={e => { e.currentTarget.style.background = isOpen ? theme.navLinkHover : 'transparent'; }}
      >
        <span style={{ fontSize: '15px', lineHeight: 1 }}>{current.flag}</span>
        <span>{current.nativeName}</span>
        <ChevronDown
          size={13}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            [isRTL ? 'left' : 'right']: 0,
            background: '#ffffff',
            borderRadius: '12px',
            border: `1px solid ${theme.cardBorder}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            zIndex: 2000,
            minWidth: '175px',
            overflow: 'hidden',
            padding: '4px',
          }}
        >
          {LANGUAGES.map(lang => {
            const isActive = lang.code === language;
            return (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setIsOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: isActive ? theme.navLinkHover : 'transparent',
                  color: isActive ? theme.primary : theme.textPrimary,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: isActive ? 600 : 400,
                  textAlign: isRTL ? 'right' : 'left',
                  transition: 'background 0.12s ease',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = theme.navLinkHover; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
